import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "simplydo/react/ui/button";

import {
  BasicRichText,
  type BasicRichTextProps,
  type EditorMenuItem,
} from "./BasicRichText";
import type { EditorProps } from "./editor";
import type { CapabilityName } from "./editor/types";
import { pickCapabilities } from "./editor/types";
import { getMentions } from "./getMentions";
import type { ChooserState } from "./presets/menuTypes";
import { presets } from "./presets/presets";

const DEFAULT_RESTRICTED_CONTENT_HEIGHT = 300;

export type RichTextProps = {
  forType?: string;
  forId?: string;
  preset?: keyof typeof presets;
  // If preset === "custom", these capability keys filter the full preset
  customPresetExtensions?: CapabilityName[];
  customPresetMenu?: CapabilityName[];
  /**
   * Appended after the preset toolbar menu (used by the mobile WebView shell
   * for native media pickers alongside the simple link menu).
   */
  additionalMenuItems?: EditorMenuItem[];
  extraData?: unknown;
  fullMentionLinks?: boolean;
  /** Passed through to the editor (e.g. `onExternalLink` for embedded WebView bridges). */
  editorSettings?: EditorProps["settings"];
  onChange?: (html: string, textValue: string, extraData: unknown) => void;
} & BasicRichTextProps;

// forType and forId are required for uploads, however in cases such as the "simple" preset no upload is possible therefore they don't need to be added
// This is currently not enforced by the type system but can be gathered by the various examples of us using this component across the app
export const RichText = ({
  value,
  forType,
  forId,
  preset = "full",
  customPresetExtensions,
  customPresetMenu,
  additionalMenuItems,
  onChange,
  extraData,
  fullMentionLinks,
  editorSettings,
  ...rest
}: RichTextProps) => {
  const [mentionState, setMentionState] = useState<{
    mentions: Record<string, unknown[]>;
    lastKind: string;
    lastQuery: string;
  } | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getThrottledMentions = useCallback((kind: string[], query: string) => {
    const kindKey = kind[0];
    if (!kindKey) return;
    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = null;
    }

    timeout.current = setTimeout(() => {
      getMentions(kind, query)
        .then((result) => {
          setMentionState({
            lastKind: kindKey,
            lastQuery: query,
            mentions: result,
          });
          timeout.current = null;
        })
        .catch(() => {});
    }, 300);
  }, []);

  const [chooser, setChooser] = useState<ChooserState>({});

  const setChooserType = useCallback(
    (kind: string, open: boolean, text?: string) =>
      setChooser(() => ({ [kind]: { open, text } })),
    [],
  );

  const handleChange = useCallback(
    (html: string, textValue: string) => {
      if (onChange) {
        return onChange(html, textValue, extraData);
      }
    },
    [onChange, extraData],
  );

  const presetChoice = useMemo(() => presets[preset], [preset]);
  const initialPresetMenu = useMemo(
    () => presetChoice.getMenu(chooser, setChooserType, forType, forId),
    [presetChoice, chooser, setChooserType, forType, forId],
  );

  const [capabilities, presetMenu] = useMemo(() => {
    let nextCapabilities = presetChoice.capabilities;
    let nextMenu = initialPresetMenu;
    if (preset === "custom" && customPresetExtensions) {
      nextCapabilities = pickCapabilities(
        nextCapabilities,
        customPresetExtensions,
      );
      nextMenu = (nextMenu as EditorMenuItem[]).filter((menu) =>
        (customPresetMenu || customPresetExtensions).includes(
          menu.name as CapabilityName,
        ),
      );
    }
    if (additionalMenuItems?.length) {
      nextMenu = [...(nextMenu as EditorMenuItem[]), ...additionalMenuItems];
    }
    return [nextCapabilities, nextMenu];
  }, [
    preset,
    presetChoice,
    customPresetExtensions,
    customPresetMenu,
    initialPresetMenu,
    additionalMenuItems,
  ]);

  return (
    <BasicRichText
      {...rest}
      value={value}
      capabilities={capabilities}
      mentions={mentionState?.mentions ?? {}}
      onSuggest={getThrottledMentions}
      extraMenuItems={presetMenu as EditorMenuItem[]}
      onChange={handleChange}
      settings={{
        ...(editorSettings ?? {}),
        ...(forType && forId ? { uploadOptions: { forType, forId } } : {}),
        fullMentionLinks: !!fullMentionLinks,
      }}
    />
  );
};

type RichTextViewerProps = Omit<BasicRichTextProps, "readOnly"> & {
  renderMention?:
    | ((args: {
        kind: string;
        id: string;
        label?: string;
        attrs: Record<string, unknown>;
      }) => React.ReactNode)
    | undefined;
  restrictInitialContentHeight?: boolean;
};

const RestrictedHeightViewer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(contentEl);
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <>
      <div
        style={{
          maxHeight: showMore
            ? contentHeight
            : DEFAULT_RESTRICTED_CONTENT_HEIGHT,
          overflow: "hidden",
          transition: "max-height 0.3s ease-in-out",
        }}
      >
        <div ref={contentRef}>{children}</div>
      </div>
      {contentHeight > DEFAULT_RESTRICTED_CONTENT_HEIGHT ? (
        <div>
          <Button
            variant="outline"
            size="small"
            onClick={() => setShowMore((prev) => !prev)}
          >
            {showMore ? t("richtext.showLess") : t("richtext.showMore")}
          </Button>
        </div>
      ) : null}
    </>
  );
};

export const RichTextViewer = ({
  restrictInitialContentHeight,
  renderMention,
  settings,
  ...props
}: RichTextViewerProps) => {
  const viewer = (
    <BasicRichText
      capabilities={presets.full.capabilities}
      {...props}
      readOnly={true}
      settings={{ ...(settings || {}), renderMention }}
    />
  );

  if (!restrictInitialContentHeight) {
    return viewer;
  }

  return <RestrictedHeightViewer>{viewer}</RestrictedHeightViewer>;
};

export const MarkdownViewer = (props: RichTextViewerProps) => {
  return <RichTextViewer {...props} markdown={true} emptyReturnValue="" />;
};
